import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useApi } from '../../api/hooks'
import { setAuthToken } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import { apiErrorCode, apiErrorMessage } from '../../lib/apiError'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { AuthLayout } from '../../components/auth/AuthLayout'

// One route, two views. The reset link mailed to the user lands here with a
// `?token=`, so the confirm form and the request form share a URL — there is no
// second page for the user to lose track of.
interface PasswordResetSearch {
    token?: string
    email?: string
}

export const Route = createFileRoute('/$org/password-reset')({
    component: PasswordResetPage,
    validateSearch: (search: Record<string, unknown>): PasswordResetSearch => ({
        token: (search.token as string) || undefined,
        email: (search.email as string) || undefined,
    }),
})

function PasswordResetPage() {
    const { token } = Route.useSearch()

    return (
        <AuthLayout>
            {token ? <ConfirmForm token={token} /> : <RequestForm />}
        </AuthLayout>
    )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="w-full max-w-sm rounded-2xl border bg-card p-8 shadow-dialog">
            <h2 className="mb-6 text-lg font-semibold text-foreground">{title}</h2>
            {children}
        </div>
    )
}

function BackToLogin({ org }: { org: string }) {
    const { t } = useTranslation('auth')
    return (
        <div className="mt-4 text-center text-sm">
            <Link
                to="/$org/login"
                params={{ org }}
                className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                data-testid="back-to-login-link"
            >
                {t('passwordReset.backToLogin')}
            </Link>
        </div>
    )
}

// --- Request view -----------------------------------------------------------

function RequestForm() {
    const { org } = Route.useParams()
    const { email: prefilledEmail } = Route.useSearch()
    const { t, i18n } = useTranslation('auth')
    const { request } = useApi()

    const [email, setEmail] = useState(prefilledEmail ?? '')
    const [submitting, setSubmitting] = useState(false)
    const [sent, setSent] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        setSubmitting(true)
        try {
            await request(
                '/api/v1/organizations/{orgCode}/auth/password-reset/request',
                'POST',
                {
                    params: { path: { orgCode: org } },
                    body: { email, lang: i18n.language?.split('-')[0] || 'en' },
                },
            )
            setSent(true)
        } catch (err) {
            // 429 is the only failure the user can act on; anything else is a
            // server-side problem they can just retry.
            setError(
                apiErrorCode(err) === 'password_reset_rate_limited'
                    ? t('passwordReset.rateLimited')
                    : apiErrorMessage(err, t('passwordReset.requestFailed')),
            )
        } finally {
            setSubmitting(false)
        }
    }

    if (sent) {
        return (
            <Card title={t('passwordReset.title')}>
                <p
                    className="text-sm text-muted-foreground"
                    data-testid="password-reset-sent"
                >
                    {t('passwordReset.sent')}
                </p>
                <BackToLogin org={org} />
            </Card>
        )
    }

    return (
        <Card title={t('passwordReset.title')}>
            <p className="text-sm text-muted-foreground mb-4">
                {t('passwordReset.description')}
            </p>
            <form
                onSubmit={onSubmit}
                className="space-y-4"
                data-testid="password-reset-request-form"
            >
                <div className="space-y-2">
                    <Label htmlFor="password-reset-email">
                        {t('passwordReset.emailLabel')}
                    </Label>
                    <Input
                        id="password-reset-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="username"
                        data-testid="password-reset-email-input"
                    />
                </div>

                {error && (
                    <div
                        className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded"
                        data-testid="password-reset-error"
                    >
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting}
                    data-testid="password-reset-submit"
                >
                    {submitting
                        ? t('passwordReset.sending')
                        : t('passwordReset.submit')}
                </Button>
            </form>
            <BackToLogin org={org} />
        </Card>
    )
}

// --- Confirm view -----------------------------------------------------------

// The server only requires a non-empty password (same rule as the invitation
// signup flow); the confirmation field is a UX guard against typos.
const confirmSchema = (messages: { required: string; mismatch: string }) =>
    z
        .object({
            password: z.string().trim().min(1, messages.required),
            confirmation: z.string(),
        })
        .refine((v) => v.password === v.confirmation, {
            message: messages.mismatch,
            path: ['confirmation'],
        })

function ConfirmForm({ token }: { token: string }) {
    const { org } = Route.useParams()
    const { t } = useTranslation('auth')
    const { request } = useApi()
    const { loginWithTokens } = useAuth()
    const navigate = useNavigate()

    const [password, setPassword] = useState('')
    const [confirmation, setConfirmation] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [tokenInvalid, setTokenInvalid] = useState(false)

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)

        const parsed = confirmSchema({
            required: t('passwordReset.passwordRequired'),
            mismatch: t('passwordReset.passwordMismatch'),
        }).safeParse({ password, confirmation })
        if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? t('passwordReset.confirmFailed'))
            return
        }

        setSubmitting(true)
        try {
            const response = (await request(
                '/api/v1/organizations/{orgCode}/auth/password-reset/confirm',
                'POST',
                {
                    params: { path: { orgCode: org } },
                    body: { token, password },
                },
            )) as {
                access_token: string
                refresh_token: string
                expires_in: number
                user: any
                default_org_code?: string
                mfa_required?: boolean
                mfa_challenge_token?: string
                mfa_enrollment_required?: boolean
            }

            // The reset proves the e-mail, not the second factor (spec
            // 2026-10-01-03): the login page asks for the code.
            if (response.mfa_required && response.mfa_challenge_token) {
                navigate({
                    to: '/$org/login',
                    params: { org },
                    search: { mfa_challenge: response.mfa_challenge_token, next: undefined, email: undefined, password: undefined },
                })
                return
            }

            // Same tail as a password login: establish the session and land the
            // user inside the org, already signed in.
            setAuthToken(response.access_token)
            loginWithTokens({
                access_token: response.access_token,
                refresh_token: response.refresh_token,
                expires_in: response.expires_in,
                user: response.user,
                orgCode: org,
                default_org_code: response.default_org_code,
                mfa_enrollment_required: response.mfa_enrollment_required,
            })
            if (response.mfa_enrollment_required) {
                navigate({ to: '/mfa-setup', search: { org, next: undefined } })
                return
            }
            navigate({ to: `/${response.default_org_code || org}` })
        } catch (err) {
            if (apiErrorCode(err) === 'password_reset_token_invalid') {
                setTokenInvalid(true)
                return
            }
            setError(apiErrorMessage(err, t('passwordReset.confirmFailed')))
        } finally {
            setSubmitting(false)
        }
    }

    if (tokenInvalid) {
        return (
            <Card title={t('passwordReset.confirmTitle')}>
                <p
                    className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded"
                    data-testid="password-reset-token-invalid"
                >
                    {t('passwordReset.tokenInvalid')}
                </p>
                <div className="mt-4">
                    <Link to="/$org/password-reset" params={{ org }} search={{}}>
                        <Button
                            variant="outline"
                            className="w-full"
                            data-testid="password-reset-restart"
                        >
                            {t('passwordReset.restart')}
                        </Button>
                    </Link>
                </div>
                <BackToLogin org={org} />
            </Card>
        )
    }

    return (
        <Card title={t('passwordReset.confirmTitle')}>
            <p className="text-sm text-muted-foreground mb-4">
                {t('passwordReset.confirmDescription')}
            </p>
            <form
                onSubmit={onSubmit}
                className="space-y-4"
                data-testid="password-reset-confirm-form"
            >
                <div className="space-y-2">
                    <Label htmlFor="password-reset-password">
                        {t('passwordReset.newPasswordLabel')}
                    </Label>
                    <Input
                        id="password-reset-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        data-testid="password-reset-password-input"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="password-reset-confirmation">
                        {t('passwordReset.confirmPasswordLabel')}
                    </Label>
                    <Input
                        id="password-reset-confirmation"
                        type="password"
                        value={confirmation}
                        onChange={(e) => setConfirmation(e.target.value)}
                        required
                        autoComplete="new-password"
                        data-testid="password-reset-confirmation-input"
                    />
                </div>

                {error && (
                    <div
                        className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded"
                        data-testid="password-reset-error"
                    >
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting}
                    data-testid="password-reset-confirm-submit"
                >
                    {submitting
                        ? t('passwordReset.saving')
                        : t('passwordReset.confirmSubmit')}
                </Button>
            </form>
            <BackToLogin org={org} />
        </Card>
    )
}
